import { ChevronsUpDown, Rows3 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '../../primitives/Button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface PageSizeFilterProps {
  /**
   * Current page size
   */
  pageSize: number
  /**
   * Callback when page size changes
   */
  onChange: (_size: number) => void
  /**
   * Available size options
   */
  options?: number[]
  /**
   * Custom label
   */
  label?: string
  /**
   * Popover alignment relative to trigger button
   * @default 'end' - maintains current behavior
   */
  popoverAlign?: 'start' | 'center' | 'end'
}

/**
 * PageSizeFilter - Selector for number of items per page
 * Reusable component to control pagination
 * Uses checkboxes like the inventory component
 */
export function PageSizeFilter({
  pageSize,
  onChange,
  options = [10, 20, 30, 40, 50],
  label,
  popoverAlign = 'end',
}: PageSizeFilterProps) {
  const [open, setOpen] = useState(false)

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'h-9 min-w-fit shrink-0 justify-start px-3 font-normal text-[#262626]',
            'border-[#D4D4D4] bg-[#FAFAFA] text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
            'hover:bg-[#F5F5F5] active:bg-[#E5E5E5]'
          )}
          icon={<Rows3 className="h-4 w-4" />}
          iconPosition="left"
          size="sm"
          variant="outline"
        >
          <span className="flex items-center gap-2">
            {label || `${pageSize} por página`}
            <ChevronsUpDown className="h-4 w-4" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={popoverAlign}
        className="w-[181px] border-[#D4D4D4] bg-[#FAFAFA] p-0 shadow-md"
        side="bottom"
        sideOffset={4}
      >
        {/* Header with label */}
        <div className="flex flex-col items-start px-2 py-0">
          <div className="flex h-9 min-h-9 w-full flex-row items-center gap-1 self-stretch">
            <span
              className="flex-grow text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]"
              style={{ fontFamily: 'Geist' }}
            >
              Resultados por páginas
            </span>
          </div>
        </div>

        {/* Separator */}
        <div className="flex h-px w-full items-center self-stretch rounded-[2px] bg-[#FAFAFA]">
          <div className="h-px w-full flex-grow bg-[#D4D4D4]" />
        </div>

        {/* Items container */}
        <div
          className="flex touch-pan-y flex-col items-start gap-2 self-stretch overflow-y-auto p-2"
          onTouchMove={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          {options.map((size) => {
            const isSelected = pageSize === size
            return (
              <button
                className={cn(
                  'flex h-8 min-h-8 w-full flex-row items-center gap-2 self-stretch rounded-md px-2 py-1',
                  isSelected ? 'bg-[#F5F5F5]' : 'bg-[#FAFAFA]',
                  'transition-colors hover:bg-[#F5F5F5]'
                )}
                key={size}
                onClick={() => {
                  onChange(size)
                  setOpen(false)
                }}
                type="button"
              >
                <div className="flex flex-row items-center gap-2">
                  <div className="flex flex-col items-start justify-center">
                    <span
                      className="text-sm font-normal leading-5 tracking-[0.005em] text-[#262626]"
                      style={{ fontFamily: 'Geist' }}
                    >
                      {size}
                    </span>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
